// Исполнительные документы человека: процент удержания и срок его действия.
//
// Процент индивидуальный: у заказчика проверены 25, 50 и 70 процентов у трёх сотрудников.
// Считается он от ПОЛНОЙ официальной суммы и вычитается до формирования траншей — иначе
// удержание уйдёт из уже выданных денег и итог разойдётся с кассой.
//
// Документ сам по себе строку периода не меняет: он говорит, какой процент действует. В строки
// периода процент проставляет действие «Проставить проценты» в шапке экрана — одно на весь
// период, чтобы не обходить 28 человек руками.

import { useState } from 'react';

import { countLabel } from '@/shared/text/plural';
import type { WritOrderOut } from '@/shared/api/endpoints/official';
import { useAuthStore } from '@/shared/auth/store';
import { can } from '@/shared/rbac/permissions';
import { formatDateOnly } from '@/shared/time/format';
import * as Badge from '@/shared/ui/badge';
import * as Button from '@/shared/ui/button';
import { DateField, todayIso } from '@/shared/ui/date-field';
import { ErrorAlert } from '@/shared/ui/error-alert';
import { ErrorState, Skeleton } from '@/shared/ui/screen-state';
import { TextField } from '@/shared/ui/text-field';

import { formatPercent } from '../columns';
import { useCreateWritOrder, useGrid, useWritOrders } from '../queries';

import { useCurrentPeriodId } from './context';

const EDIT_PERMISSION = 'core.official.edit';

export function WritOrdersPanel({ id }: { id: string }) {
  const права = useAuthStore((state) => state.permissions);
  const документы = useWritOrders(id, false);
  const periodId = useCurrentPeriodId();
  const сетка = useGrid(periodId, {});
  const строка = сетка.data?.rows.find((пункт) => пункт.employee_id === id) ?? null;

  const имя = документы.data?.items[0]?.employee_name ?? строка?.employee_name ?? null;
  const список = документы.data?.items ?? [];

  return (
    <div className='flex flex-col gap-4'>
      <div className='flex flex-col gap-1'>
        <h3 className='text-label-lg text-text-strong-950'>{имя ?? 'Человек'}</h3>
        <span className='text-paragraph-xs text-text-sub-600'>
          Процент считается от полной официальной суммы и вычитается до формирования траншей.
        </span>
      </div>

      {документы.error !== null && (
        <ErrorState
          subject='исполнительные документы человека'
          error={документы.error as never}
          onRetry={() => void документы.refetch()}
        />
      )}

      {документы.isPending ? (
        <Skeleton className='h-24 w-full rounded-lg' />
      ) : список.length === 0 ? (
        <p className='text-paragraph-sm text-text-sub-600'>
          Исполнительных документов у этого человека нет. Пока их нет, удержание не считается, и вся
          официальная сумма идёт к выдаче.
        </p>
      ) : (
        <>
          <h4 className='text-label-sm text-text-strong-950'>
            {countLabel(список.length, [
              'исполнительный документ',
              'исполнительных документа',
              'исполнительных документов',
            ])}
          </h4>
          <ul className='flex flex-col gap-2'>
            {список.map((документ) => (
              <Документ key={документ.id} документ={документ} />
            ))}
          </ul>
        </>
      )}

      {can(права, EDIT_PERMISSION) && <ЗавестиДокумент employeeId={id} />}
    </div>
  );
}

function Документ({ документ }: { документ: WritOrderOut }) {
  return (
    <li className='flex flex-col gap-1 rounded-lg border border-stroke-soft-200 px-3 py-2'>
      <div className='flex items-center justify-between gap-2'>
        <span className='text-label-sm tabular-nums text-text-strong-950'>
          {formatPercent(документ.percent)}
        </span>
        <Badge.Root size='small' variant='light' color={документ.is_active ? 'green' : 'gray'}>
          {документ.is_active ? 'Действует' : 'Не действует'}
        </Badge.Root>
      </div>
      <span className='text-paragraph-xs text-text-sub-600'>{документ.basis}</span>
      <span className='text-paragraph-xs text-text-sub-600'>
        {документ.document_number === null ? 'без номера' : `№ ${документ.document_number}`}
        {' · с '}
        {formatDateOnly(документ.date_from)}
        {документ.date_to === null ? ' бессрочно' : ` по ${formatDateOnly(документ.date_to)}`}
      </span>
    </li>
  );
}

function ЗавестиДокумент({ employeeId }: { employeeId: string }) {
  const операция = useCreateWritOrder();
  const [форма, setФорма] = useState(false);
  const [процент, setПроцент] = useState('');
  const [номер, setНомер] = useState('');
  const [основание, setОснование] = useState('');
  const [с, setС] = useState(todayIso);
  const [по, setПо] = useState('');
  const [ошибки, setОшибки] = useState<{ процент?: string; основание?: string }>({});

  if (!форма) {
    return (
      <Button.Root variant='neutral' mode='stroke' size='xsmall' onClick={() => setФорма(true)}>
        Завести исполнительный документ
      </Button.Root>
    );
  }

  return (
    <div className='flex flex-col gap-2 border-t border-stroke-soft-200 pt-4'>
      <TextField
        id='writ-percent'
        label='Процент удержания'
        required
        value={процент}
        onChange={setПроцент}
        placeholder='70'
        inputMode='decimal'
        error={ошибки.процент}
        hint='От 0 до 100. Считается от полной официальной суммы человека.'
      />
      <TextField
        id='writ-number'
        label='Номер документа'
        value={номер}
        onChange={setНомер}
        placeholder='ИП 12345/26/86018'
      />
      <TextField
        id='writ-basis'
        label='Основание'
        required
        value={основание}
        onChange={setОснование}
        placeholder='постановление судебного пристава об удержании алиментов'
        error={ошибки.основание}
      />
      <DateField id='writ-from' label='Действует с' required value={с} onChange={setС} />
      <DateField
        id='writ-to'
        label='Действует по'
        value={по}
        onChange={setПо}
        hint='Пусто — бессрочно.'
      />
      <div className='flex gap-2'>
        <Button.Root
          variant='primary'
          mode='filled'
          size='xsmall'
          onClick={() => {
            const число = Number(процент.replace(',', '.'));
            const следующие: { процент?: string; основание?: string } = {};
            if (!Number.isFinite(число) || число <= 0 || число > 100) {
              следующие.процент = 'Процент пишется числом от 0 до 100: 25, 50, 70.';
            }
            if (основание.trim().length < 3) {
              следующие.основание =
                'Основание обязательно: удержание из зарплаты без записанной причины не объяснить ни человеку, ни проверке.';
            }
            setОшибки(следующие);
            if (Object.keys(следующие).length > 0) {
              return;
            }
            операция.mutate(
              {
                employee_id: employeeId,
                percent: процент.replace(',', '.'),
                basis: основание.trim(),
                date_from: с,
                date_to: по === '' ? null : по,
                document_number: номер.trim() === '' ? null : номер.trim(),
              },
              {
                onSuccess: () => {
                  setФорма(false);
                  setПроцент('');
                  setНомер('');
                  setОснование('');
                  setПо('');
                },
              },
            );
          }}
        >
          {операция.isPending ? 'Заводим…' : 'Завести документ'}
        </Button.Root>
        <Button.Root variant='neutral' mode='stroke' size='xsmall' onClick={() => setФорма(false)}>
          Отмена
        </Button.Root>
      </div>
      <p className='text-paragraph-xs text-text-sub-600'>
        Документ сам по себе строки периода не меняет. Чтобы процент встал в строки, нажмите
        «Проставить проценты» в шапке экрана.
      </p>
      {операция.error !== null && <ErrorAlert error={операция.error} />}
    </div>
  );
}
